import clsx from "clsx"
import { memo, useState } from "react"
import { shallowEqual } from "react-redux"
import useAsyncEffect from "use-async-effect"

import { useAppSelector } from "@/App"
import { Card } from "@/components/Card"
import { DriverHeader } from "@/components/DriverHeader"
import panelCls from "@/components/Panel/Panel.module.scss"
import { EventsTimeline } from "@/modules/EventsTimeline"
import { Order, OrderFull, Team } from "@/types"
import { fetchOrderTimeline } from "@/utils"

import cls from "./OrderHistory.module.scss"

type OrderHistoryProps = {
  order: Order
}

export const OrderHistory = memo(({ order }: OrderHistoryProps) => {
  const events = useOrderTimeline(order.team_id, order.id)

  return (
    <Card className={panelCls.card}>
      <Card.Section>
        <DriverHeader
          orderId={order.id}
          driverId={order.driver_id}
          subheader={
            <span className={cls.subheader}>
              <span>{order.distance} km</span>
            </span>
          }
        />
      </Card.Section>

      <Card.Section>
        <EventsTimeline
          events={events}
          textKey={(event) => event.description.support_text}
          parameters={(event, paramCls) => {
            if (!event.description?.support_parameters) return null

            return Object.entries(event.description.support_parameters).map(([key, value]) => (
              <div key={key} className={clsx(paramCls, ["delivery_late", "pickup_late"].includes(key) && "text-alert")}>
                {value}
              </div>
            ))
          }}
        />
      </Card.Section>
    </Card>
  )
})

function useOrderTimeline(teamId: Team["id"], orderId: Order["id"]) {
  const [timeline, setTimeline] = useState<"loading" | "error" | OrderFull["events"]>("loading")

  const currentSite = useAppSelector((state) => state.global.currentSite, shallowEqual)

  useAsyncEffect(
    async (isSubscribed) => {
      try {
        const {
          response,
          json: { data },
        } = await fetchOrderTimeline(teamId, orderId)
        if (!response.ok) throw response

        const events = data?.events
        if (events && isSubscribed()) setTimeline(events)
      } catch (error) {
        if (isSubscribed()) setTimeline("error")
      }
    },
    [orderId, teamId, currentSite]
  )

  return timeline
}
